import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { SpotlightTransition } from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';

import {
	createSingleQueryParamHook,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../controller/shareAndRestrictDialogConfig.ts';

const useJourneyIdQueryParam = createSingleQueryParamHook('journeyId');

export function OnboardingPagePermissionsSpotlight({
	onClose,
	journeyId,
}: {
	onClose: () => void;
	journeyId?: string;
}) {
	const { Spotlight } = useChoreographerAssets();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'onboardingPagePermissionsSpotlight',
				attributes: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					journeyId,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, journeyId]);

	return (
		<Spotlight
			actions={[
				{
					onClick: onClose,
					text: (
						<FormattedMessage
							id="share-and-restrict-dialog.share-and-restrict-spotlight-onboarding.ok"
							defaultMessage="Ok"
							description="CTA which dismisses the spotlight"
						/>
					),
				},
			]}
			target="share-button-spotlight-target"
			targetRadius={3}
			targetBgColor={token('elevation.surface')}
			dialogPlacement="bottom right"
			dialogWidth={275}
			messageType="transactional"
		>
			<FormattedMessage
				id="share-and-restrict-dialog.share-and-restrict-spotlight-onboarding.body"
				defaultMessage="Control who can view, edit, and comment on your pages."
				description="Inform the customer that they can click this button to control permissions of the current page"
			/>
		</Spotlight>
	);
}

export function OnboardingPagePermissionsSpotlightLoader() {
	const { setQueryParams } = useRouteActions();
	const journeyId = useJourneyIdQueryParam();
	const [isSpotlightActive, setIsSpotlightActive] = useState(false);
	const hasRun = useRef(false);
	const [cachedJourneyId, setCachedJourneyId] = useState<string | undefined>();

	useEffect(() => {
		if (hasRun.current) {
			// Ensure the customer never see's more than once due to a re-render or query param bug
			return;
		}

		hasRun.current = true;

		if (journeyId !== 'onboarding-page-permissions') {
			// We are only matching on the specific query param
			return;
		}

		setCachedJourneyId(journeyId);

		// Clear the query param to ensure that the URL when shared doesn't include this behavior
		setQueryParams({ journeyId: null });

		setIsSpotlightActive(true);
	}, [journeyId, setQueryParams]);

	const handleOnClose = useCallback(() => setIsSpotlightActive(false), []);

	return (
		<SpotlightTransition>
			{isSpotlightActive && (
				<OnboardingPagePermissionsSpotlight onClose={handleOnClose} journeyId={cachedJourneyId} />
			)}
		</SpotlightTransition>
	);
}
